import React, {
  useRef,
  useLayoutEffect,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useId,
} from 'react';

// MUI Components
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import { Typography, Box, Input, Divider, Tooltip } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';

// Components
import TruncateTextFixedLength from '../TruncatedText';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';

// Assets
import { SelectCloseCIcon } from '~/assets/images';

import { focusAdjacentField } from '~/utils/focus-navigation';
import {
  getDropdownMenuWidth,
  getDropdownMenuWidthStyles,
  getDropdownSearchPlaceholder,
} from '~/utils/dropdown-menu';

// Styles
import './styles.scss';

// Constants
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxWidth: 'calc(100vw - 32px)',
      overflow: 'hidden',
    },
  },
};

const OlcDropdownSearch = (props) => {
  const {
    options = [],
    selectedValue = null,
    setSelectedValue,
    component,
    onSearchChange = undefined,
    simpleSelect = false,
    multiple = false,
    placeHolderText = '',
    searchFieldPlaceholderText = '',
    noOptionText = 'No options',
    disabled = false,
    searchAutocomplete = false,
    type = '',
    setCreateTagModal = () => {},
    customIcon = false,
    className = '',
    activeState = false,
    error = false,
    clearable = false,
    searchable = true,
    selectClassName = '',
    controlVariant = 'default',
    showCreateOption = true,
    menuPaperClassName = '',
    menuZIndex = undefined,
    menuDisableFocusTrap = false,
    showSelectedValueTooltip = false,
    footerAction = null,
    // Optional external control over search loading indicator
    searchLoading = undefined,
  } = props;

  // Helper to check if component uses ID-based values
  const usesIdBasedValues = useMemo(() => component === 'multipleIds', [component]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(null);
  const dropdownId = useId();
  const searchInputId = `${dropdownId}-search`;
  const menuListRef = useRef(null);
  const fieldRootRef = useRef(null);
  const scrollTopRef = useRef(0);

  const [search, setSearch] = useState('');
  const searchInputRef = useRef(null);
  const [internalSearchLoading, setInternalSearchLoading] = useState(false);

  const findOptionByValue = useCallback(
    (val) => {
      const optionObject = options.find((opt) => {
        return usesIdBasedValues ? opt.id === val : opt.title === val;
      });
      return optionObject || { id: val, title: val };
    },
    [options, usesIdBasedValues]
  );

  // handle change (select). Keep your previous protections for search input clicks.
  const handleChange = (event, isSearch = false) => {
    const {
      target: { value },
    } = event;

    if (isSearch === true || isSearch?.key === '.$searchFieldInput') return;
    if (value === '__footer_action__') return;

    if (menuListRef.current) {
      scrollTopRef.current = menuListRef.current.scrollTop;
    }

    // For single select, value is directly the selected raw value (id or title)
    const rawSelectedValue = value;

    const selectedOption = options.find((opt) => {
      if (usesIdBasedValues) {
        return opt.id === rawSelectedValue;
      } else {
        return opt.title === rawSelectedValue;
      }
    });

    if (selectedOption?.func) {
      // run custom func (create tag)
      selectedOption.func();
      return;
    }

    // For campaigns/templates, pass through the full option (mirrors GenericAutocomplete behavior)
    if (selectedOption) {
      setSelectedValue(selectedOption);
      return;
    }
    // Fallback: minimal object with id and title
    setSelectedValue({
      id: rawSelectedValue,
      title: String(rawSelectedValue),
    });
  };

  const handleMenuOpen = () => {
    setMenuWidth(getDropdownMenuWidth(fieldRootRef.current));
    setMenuOpen(true);
  };

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    setSearch(''); // clear search when closing
    if (typeof onSearchChange === 'function') {
      onSearchChange('');
    }
    setInternalSearchLoading(false);
  }, [onSearchChange]);

  const handleFooterActionClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      handleMenuClose();
      footerAction?.onClick?.();
    },
    [footerAction, handleMenuClose]
  );

  useEffect(() => {
    if (!menuOpen) return undefined;

    const anchorRect = fieldRootRef.current?.getBoundingClientRect();

    const handleOutsideScroll = (event) => {
      const scrollTarget = event.target;

      // Scrolling the options is part of using the dropdown. Any other scroll
      // moves the trigger away from its menu, so close the menu instead of
      // leaving a detached overlay behind.
      if (scrollTarget?.nodeType && menuListRef.current?.contains(scrollTarget)) {
        return;
      }

      handleMenuClose();
    };

    const watchAnchorPosition = () => {
      const nextAnchorRect = fieldRootRef.current?.getBoundingClientRect();

      if (
        anchorRect &&
        nextAnchorRect &&
        (Math.abs(nextAnchorRect.top - anchorRect.top) > 0.5 ||
          Math.abs(nextAnchorRect.left - anchorRect.left) > 0.5)
      ) {
        handleMenuClose();
      }
    };

    document.addEventListener('scroll', handleOutsideScroll, true);
    window.addEventListener('scroll', handleOutsideScroll, true);
    window.addEventListener('wheel', handleOutsideScroll, {
      capture: true,
      passive: true,
    });
    window.addEventListener('touchmove', handleOutsideScroll, {
      capture: true,
      passive: true,
    });
    const positionCheckId = window.setInterval(watchAnchorPosition, 25);

    return () => {
      window.clearInterval(positionCheckId);
      document.removeEventListener('scroll', handleOutsideScroll, true);
      window.removeEventListener('scroll', handleOutsideScroll, true);
      window.removeEventListener('wheel', handleOutsideScroll, true);
      window.removeEventListener('touchmove', handleOutsideScroll, true);
    };
  }, [handleMenuClose, menuOpen]);

  const handleClear = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setSelectedValue?.(null);
      setSearch('');
      onSearchChange?.('');
    },
    [onSearchChange, setSelectedValue]
  );

  // Compute filteredOptions from options and search only (no sorting/reordering)
  const filteredOptions = useMemo(() => {
    const searchLower = (search || '').toLowerCase();

    const optionsInSearch = (options || []).filter((item) =>
      (item?.title || '').toLowerCase().includes(searchLower)
    );

    // If nothing matches and we're in "templates" mode, show Create New Template option
    const isTemplateMode =
      String(searchFieldPlaceholderText || '')
        .toLowerCase()
        .includes('template') ||
      String(placeHolderText || '')
        .toLowerCase()
        .includes('template') ||
      component === 'templates' ||
      type === 'templates';
    // If nothing matches and we're in "campaigns" mode, show Create New Campaign option
    const isCampaignMode =
      String(searchFieldPlaceholderText || '')
        .toLowerCase()
        .includes('campaign') ||
      String(placeHolderText || '')
        .toLowerCase()
        .includes('campaign') ||
      component === 'campaigns' ||
      type === 'campaigns';
    // If nothing matches and we're in "return address" mode, show special options
    const isReturnAddressMode =
      String(searchFieldPlaceholderText || '')
        .toLowerCase()
        .includes('return address') ||
      String(placeHolderText || '')
        .toLowerCase()
        .includes('return address') ||
      component === 'returnAddress' ||
      type === 'returnAddress';

    if (showCreateOption && optionsInSearch.length === 0 && search?.trim() && isTemplateMode) {
      const existingCreateOpt =
        (options || []).find(
          (opt) =>
            String(opt?.id) === '-1' ||
            (opt?.custom && /create new template/i.test(opt?.title || ''))
        ) || null;
      return existingCreateOpt
        ? [existingCreateOpt]
        : [{ id: '-1', title: 'Create New Template', custom: true }];
    }
    if (showCreateOption && optionsInSearch.length === 0 && search?.trim() && isCampaignMode) {
      const existingCreateCampaignOpt =
        (options || []).find(
          (opt) =>
            String(opt?.id) === '-1' ||
            (opt?.custom && /create new campaign/i.test(opt?.title || ''))
        ) || null;
      return existingCreateCampaignOpt
        ? [existingCreateCampaignOpt]
        : [{ id: '-1', title: 'Create New Campaign', custom: true }];
    }
    if (optionsInSearch.length === 0 && search?.trim() && isReturnAddressMode) {
      const noReturnOpt = (options || []).find(
        (opt) => String(opt?.id) === '-1' || /no return address/i.test(opt?.title || '')
      ) || { id: '-1', title: '-No Return Address-', custom: true, hideDividerAbove: true };
      const createSenderOpt = (options || []).find(
        (opt) => String(opt?.id) === '-2' || /create new sender profile/i.test(opt?.title || '')
      ) || { id: '-2', title: 'Create New Sender Profile', custom: true };
      return [noReturnOpt, createSenderOpt];
    }

    return optionsInSearch;
  }, [
    options,
    search,
    usesIdBasedValues,
    showCreateOption,
    component,
    type,
    placeHolderText,
    searchFieldPlaceholderText,
  ]);

  // maintain scroll position when menu opens/closes
  useLayoutEffect(() => {
    if (menuOpen && menuListRef.current) {
      menuListRef.current.scrollTop = scrollTopRef.current;
    }
  }, [menuOpen]);

  // keep the search input focused while menu is open and search changes
  useEffect(() => {
    if (menuOpen && searchable && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [menuOpen, search, searchable]);

  const effectiveSearchLoading =
    (typeof searchLoading === 'boolean' ? searchLoading : internalSearchLoading) && menuOpen;
  const searchPlaceholder = getDropdownSearchPlaceholder(
    searchFieldPlaceholderText,
    placeHolderText
  );
  const hasClearableValue = clearable && Boolean(selectedValue?.title);

  return (
    <FormControl
      ref={fieldRootRef}
      className={`olcDropdownSearch olcDropdownSearch--${controlVariant} multiSelectRedesign ${className}`}
    >
      <Select
        id={dropdownId}
        inputProps={{
          'aria-label': placeHolderText || searchFieldPlaceholderText || 'Select option',
        }}
        multiple={false}
        displayEmpty
        open={menuOpen}
        value={usesIdBasedValues ? selectedValue?.id || '' : selectedValue?.title || ''}
        disabled={disabled}
        onChange={handleChange}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        input={<OutlinedInput />}
        renderValue={() => {
          // For single select, show the selected option's title (from object)
          let displayText = placeHolderText;
          if (selectedValue?.title) {
            displayText = selectedValue.title;
          }

          const selectedText = (
            <Typography
              className={`olcDropdownContent ${selectedValue?.title ? 'selectedValue' : 'default'}`}
            >
              {displayText}
            </Typography>
          );

          return (
            <Box className="olcDropdownWrapper">
              {showSelectedValueTooltip && selectedValue?.title ? (
                <Tooltip title={selectedValue.title} placement="bottom-start">
                  {selectedText}
                </Tooltip>
              ) : (
                selectedText
              )}
              {clearable && selectedValue?.title ? (
                <Box
                  component="button"
                  type="button"
                  aria-label={`Clear ${placeHolderText || 'selection'}`}
                  className="selectClearIcon"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={handleClear}
                >
                  <img src={SelectCloseCIcon} alt="" />
                </Box>
              ) : null}
            </Box>
          );
        }}
        MenuProps={{
          ...MenuProps,
          disableScrollLock: true,
          disableAutoFocusItem: true,
          disableAutoFocus: menuDisableFocusTrap,
          disableEnforceFocus: menuDisableFocusTrap,
          disableRestoreFocus: menuDisableFocusTrap,
          autoFocus: false,
          sx: menuZIndex ? { zIndex: menuZIndex } : undefined,
          PaperProps: {
            ...MenuProps.PaperProps,
            style: {
              ...MenuProps.PaperProps.style,
              ...getDropdownMenuWidthStyles(menuWidth),
            },
          },
          classes: {
            paper: [
              'olcDropdownSearchOptions',
              searchable ? 'olcSearchableMenu' : '',
              menuPaperClassName,
            ].filter(Boolean).join(' '),
          },
          MenuListProps: {
            ref: menuListRef,
            onMouseDown: (e) => e.stopPropagation(),
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              overflowY: 'auto',
            },
          },
        }}
        IconComponent={(props) => (
          <OlcDropdownChevron
            {...props}
            open={menuOpen}
            style={{
              ...props.style,
              marginTop: '-2.5px',
              right: '20px',
              top: '50%',
            }}
          />
        )}
        className={`basicSelect ${selectClassName} ${hasClearableValue ? 'basicSelectClearable' : ''} ${error ? 'selectError' : ''}`}
      >
        {searchable ? (
          <Box
            style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}
            key="searchFieldInput"
            className="selectSearchInputWrapper"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {effectiveSearchLoading ? (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: '-1px',
                  left: 0,
                  right: 0,
                  height: '2px',
                  borderTopLeftRadius: '6px',
                  borderTopRightRadius: '6px',
                  pointerEvents: 'none',
                  backgroundColor: 'transparent',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#ed5c2f',
                  },
                }}
              />
            ) : null}
            <Input
              placeholder={searchPlaceholder}
              fullWidth
              autoFocus
              inputRef={searchInputRef}
              onChange={(e) => {
                const nextValue = e.target.value;
                setSearch(nextValue);
                if (typeof onSearchChange === 'function') {
                  try {
                    const result = onSearchChange(nextValue);
                    if (result && typeof result.then === 'function') {
                      setInternalSearchLoading(true);
                      Promise.resolve(result)
                        .catch(() => {})
                        .finally(() => setInternalSearchLoading(false));
                    }
                  } catch (_) {
                    // Keep the dropdown responsive if a consumer search callback fails.
                  }
                }
              }}
              value={search}
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMenuClose();
                  focusAdjacentField(fieldRootRef.current, e.shiftKey);
                  return;
                }
                e.stopPropagation();
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              key="searchFieldInput"
              id={searchInputId}
              className="selectSearchInput"
            />
          </Box>
        ) : null}

        {filteredOptions?.length > 0 ? (
          filteredOptions.map(({ id, title, disabled, icon }, idx) => {
            const itemValue = usesIdBasedValues ? id : title;
            // Context-aware create option detection (template/campaign only)
            const normalizedComponent = String(component || '').toLowerCase();
            const normalizedType = String(type || '').toLowerCase();
            const normalizedPH = String(placeHolderText || '').toLowerCase();
            const normalizedSearchPH = String(searchFieldPlaceholderText || '').toLowerCase();
            const isTemplateCtx = [
              normalizedComponent,
              normalizedType,
              normalizedPH,
              normalizedSearchPH,
            ].some((s) => s.includes('template'));
            const isCampaignCtx = [
              normalizedComponent,
              normalizedType,
              normalizedPH,
              normalizedSearchPH,
            ].some((s) => s.includes('campaign'));
            const isReturnAddrCtx = [
              normalizedComponent,
              normalizedType,
              normalizedPH,
              normalizedSearchPH,
            ].some((s) => s.includes('return address'));
            const isReturnAddrSpecialOption =
              isReturnAddrCtx &&
              (String(id) === '-1' ||
                String(id) === '-2' ||
                (typeof title === 'string' &&
                  (/no return address/i.test(title) || /create new sender profile/i.test(title))));
            const isCreateSenderProfileOption =
              isReturnAddrCtx &&
              (String(id) === '-2' ||
                (typeof title === 'string' && /create new sender profile/i.test(title)));
            const isCreateTemplateOption =
              (isTemplateCtx &&
                (String(id) === '-1' ||
                  (typeof title === 'string' && /create new template/i.test(title)))) ||
              (isCampaignCtx &&
                (String(id) === '-1' ||
                  (typeof title === 'string' && /create new campaign/i.test(title))));
            const isLast = idx === filteredOptions.length - 1;

            return isCreateSenderProfileOption ? (
              [
                <Divider
                  className="searchableMenuDivider"
                  key={`divider-return-${id}`}
                />,
                <MenuItem
                  className="searchableMenuAction"
                  key={`item-${id}`}
                  disabled={disabled === true}
                  disableRipple
                  value={itemValue}
                >
                  {icon && customIcon ? (
                    <img src={icon} alt="icon" style={{ padding: '10px' }} />
                  ) : null}
                  <ListItemText
                    primary={<TruncateTextFixedLength text={title} maxLength={20} />}
                    className={
                      (isCreateTemplateOption && isLast) || isReturnAddrSpecialOption
                        ? 'createNewTemplateOptionText'
                        : ''
                    }
                  />
                </MenuItem>,
              ]
            ) : isCreateTemplateOption && isLast ? (
              [
                <Divider className="searchableMenuDivider" key={`divider-${id}`} />,
                <MenuItem
                  className="searchableMenuAction"
                  key={`item-${id}`}
                  disabled={disabled === true}
                  disableRipple
                  value={itemValue}
                >
                  {icon && customIcon ? (
                    <img src={icon} alt="icon" style={{ padding: '10px' }} />
                  ) : null}
                  <ListItemText
                    primary={<TruncateTextFixedLength text={title} maxLength={20} />}
                    className={
                      (isCreateTemplateOption && isLast) || isReturnAddrSpecialOption
                        ? 'createNewTemplateOptionText'
                        : ''
                    }
                  />
                </MenuItem>,
              ]
            ) : (
              <MenuItem
                key={`item-${id}`}
                disabled={disabled === true}
                disableRipple
                value={itemValue}
              >
                {icon && customIcon ? (
                  <img src={icon} alt="icon" style={{ padding: '10px' }} />
                ) : null}
                <ListItemText
                  primary={<TruncateTextFixedLength text={title} maxLength={20} />}
                  className={
                    (isCreateTemplateOption && isLast) || isReturnAddrSpecialOption
                      ? 'createNewTemplateOptionText'
                      : ''
                  }
                />
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled className="noOptionsText">
            <Typography>{noOptionText}</Typography>
          </MenuItem>
        )}
        {footerAction?.label
          ? [
              <Divider
                className="searchableMenuDivider"
                key="footer-action-divider"
              />,
              <MenuItem
                className="searchableMenuAction"
                key="footer-action"
                disableRipple
                value="__footer_action__"
                onClick={handleFooterActionClick}
              >
                <ListItemText
                  primary={<TruncateTextFixedLength text={footerAction.label} maxLength={28} />}
                  className="createNewTemplateOptionText"
                />
              </MenuItem>,
            ]
          : null}
      </Select>
    </FormControl>
  );
};

export default OlcDropdownSearch;
